import { Link, getRouteApi, useBlocker, useRouter } from '@tanstack/react-router';
import { useId, useMemo, useRef, useState } from 'react';
import {
  FIELD_TYPES,
  ROLE_NAMES,
  SHEET_ROLES,
  isBuiltIn,
  javaLines,
  javaStrip,
  keyFor,
  sheetField,
  sheetSection,
  sheetTemplate,
  templateProblems,
  type SheetField,
  type SheetFieldType,
  type SheetRole,
  type SheetTemplate,
} from '@realmery/realm-core';
import { realms } from '../../db/client.ts';
import { realmFile } from '../../db/protocol.ts';
import { recordRoll } from '../../dice/diceStore.ts';
import { SheetForm } from './SheetForm.tsx';
import { conditionsFor } from './SheetPage.tsx';
import { message } from './SheetsPage.tsx';

/**
 * Builds and edits a character sheet template, as the desktop's `SheetTemplateEditor`: its
 * outline of sections and fields on one side, whatever is selected on the other, checked by
 * the desktop's own rules as you go, and a working preview that saves nothing. Renaming a
 * field's key renames it in every formula and roll, and - on save - in every sheet already
 * using the template, so nothing written on them is lost.
 */

const route = getRouteApi('/_app/realm/$realmKey/templates/$templateId');

interface FieldDraft {
  readonly uid: number;
  field: SheetField;
  /** Its key when loaded or last saved; null for a field added since. */
  originalKey: string | null;
}

interface SectionDraft {
  readonly uid: number;
  title: string;
  columns: number;
  fields: FieldDraft[];
}

interface Draft {
  name: string;
  system: string;
  description: string;
  license: string;
  /** One per line. */
  conditions: string;
  roles: Partial<Record<SheetRole, string>>;
  sections: SectionDraft[];
}

type Selection = { kind: 'template' } | { kind: 'section'; uid: number } | { kind: 'field'; uid: number };

let nextUid = 1;

function draftOf(template: SheetTemplate): Draft {
  return {
    name: template.name,
    system: template.system,
    description: template.description,
    license: template.license,
    conditions: conditionsFor(template).join('\n'),
    roles: { ...template.roles },
    sections: template.sections.map((section) => ({
      uid: nextUid++,
      title: section.title,
      columns: section.columns,
      fields: section.fields.map((field) => ({ uid: nextUid++, field, originalKey: field.key })),
    })),
  };
}

const cloneDraft = (draft: Draft): Draft => ({
  ...draft,
  roles: { ...draft.roles },
  sections: draft.sections.map((s) => ({ ...s, fields: s.fields.map((f) => ({ ...f })) })),
});

function toTemplate(draft: Draft, base: SheetTemplate): SheetTemplate {
  return sheetTemplate(
    base.id,
    {
      name: draft.name,
      system: draft.system,
      description: draft.description,
      license: draft.license,
      builtinKey: base.builtinKey,
      sections: draft.sections.map((s) => sheetSection(s.title, s.columns, s.fields.map((f) => tidy(f.field)))),
      roles: draft.roles,
      conditions: javaLines(draft.conditions).map(javaStrip).filter((c) => c !== ''),
    },
    base.created,
    base.modified,
  );
}

const allDrafts = (draft: Draft) => draft.sections.flatMap((s) => s.fields);

/** A field as typed (labels keep their spaces while being typed), tidied as the desktop's `SheetField` is. */
const tidy = (f: SheetField) => sheetField(f.key, f.label, f.type, f.defaultValue, f.formula, f.roll, f.options, f.help);

function uniqueKeyExcept(draft: Draft, base: string, except: number | null): string {
  const taken = new Set(allDrafts(draft).filter((f) => f.uid !== except).map((f) => f.field.key));
  let candidate = base;
  for (let n = 2; taken.has(candidate); n++) candidate = `${base}_${n}`;
  return candidate;
}

/** Rewrites `@old` to `@new` in every formula and roll, so renaming a key doesn't break them. */
function renameReferences(draft: Draft, oldKey: string, newKey: string): void {
  if (oldKey === '' || newKey === '' || oldKey === newKey) return;
  const reference = new RegExp(`@${oldKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![A-Za-z0-9_])`, 'g');
  const swap = (text: string) => text.replace(reference, () => `@${newKey}`);
  for (const f of allDrafts(draft)) {
    const field = f.field;
    f.field = { ...field, formula: swap(field.formula), roll: swap(field.roll), defaultValue: field.type === 'DICE' ? swap(field.defaultValue) : field.defaultValue };
  }
  for (const role of SHEET_ROLES) if (draft.roles[role] === oldKey) draft.roles[role] = newKey;
}

function sectionIndexOf(draft: Draft, selection: Selection): number {
  if (selection.kind === 'section') return draft.sections.findIndex((s) => s.uid === selection.uid);
  if (selection.kind === 'field') return draft.sections.findIndex((s) => s.fields.some((f) => f.uid === selection.uid));
  return draft.sections.length - 1;
}

function move(draft: Draft, selection: Selection, delta: number): void {
  if (selection.kind === 'section') {
    const index = draft.sections.findIndex((s) => s.uid === selection.uid);
    const target = index + delta;
    if (index >= 0 && target >= 0 && target < draft.sections.length) draft.sections.splice(target, 0, ...draft.sections.splice(index, 1));
  } else if (selection.kind === 'field') {
    for (let s = 0; s < draft.sections.length; s++) {
      const fields = draft.sections[s]!.fields;
      const index = fields.findIndex((f) => f.uid === selection.uid);
      if (index < 0) continue;
      const target = index + delta;
      if (target >= 0 && target < fields.length) fields.splice(target, 0, ...fields.splice(index, 1));
      // Past the top or bottom of its section: into the next one along.
      else if (target < 0 && s > 0) draft.sections[s - 1]!.fields.push(...fields.splice(index, 1));
      else if (target >= fields.length && s < draft.sections.length - 1) draft.sections[s + 1]!.fields.unshift(...fields.splice(index, 1));
      return;
    }
  }
}

export function TemplateEditorPage() {
  const found = route.useLoaderData();
  const { realmKey } = route.useParams();
  if (!found) {
    return (
      <article className="document">
        <h1>No such template</h1>
        <p>
          It may have been deleted.{' '}
          <Link to="/realm/$realmKey/sheets" params={{ realmKey }}>
            All sheets
          </Link>
        </p>
      </article>
    );
  }
  return <TemplateEditor key={found.template.id} template={found.template} sheetCount={found.sheetCount} />;
}

function TemplateEditor({ template, sheetCount }: { template: SheetTemplate; sheetCount: number }) {
  const { realmKey } = route.useParams();
  const file = realmFile(realmKey);
  const router = useRouter();
  const [draft, setDraft] = useState(() => draftOf(template));
  const [dirty, setDirty] = useState(false);
  const [selection, setSelection] = useState<Selection>({ kind: 'template' });
  const [preview, setPreview] = useState<ReadonlyMap<string, string> | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const built = useMemo(() => toTemplate(draft, template), [draft, template]);
  const problems = useMemo(() => templateProblems(built), [built]);

  const blocker = useBlocker({ shouldBlockFn: () => dirty, withResolver: true, enableBeforeUnload: () => dirty });

  /** Every change builds on the latest draft; `work` must read nothing from the event, which is gone by the time it runs. */
  function change(work: (next: Draft) => void) {
    setDraft((latest) => {
      const next = cloneDraft(latest);
      work(next);
      return next;
    });
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    setError('');
    const renames: Record<string, string> = {};
    for (const f of allDrafts(draft)) if (f.originalKey !== null && f.originalKey !== f.field.key) renames[f.originalKey] = f.field.key;
    try {
      await realms.updateTemplate(file, template.id, { name: built.name, system: built.system, description: built.description, license: built.license, sections: built.sections, roles: built.roles, conditions: built.conditions }, renames);
      setDraft((d) => ({ ...d, sections: d.sections.map((s) => ({ ...s, fields: s.fields.map((f) => ({ ...f, originalKey: f.field.key })) })) }));
      setDirty(false);
      await router.invalidate();
    } catch (e) {
      setError(message(e, 'The template could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  const newField = (next: Draft, uid: number): FieldDraft => ({ uid, field: sheetField(uniqueKeyExcept(next, 'new_field', null), 'New Field', 'NUMBER'), originalKey: null });
  const selectedSection = selection.kind === 'section' ? draft.sections.find((s) => s.uid === selection.uid) : undefined;
  const selectedField = selection.kind === 'field' ? allDrafts(draft).find((f) => f.uid === selection.uid) : undefined;

  return (
    <article className="document template-editor">
      <p className="eyebrow">
        <Link to="/realm/$realmKey/sheets" params={{ realmKey }}>
          ← All sheets
        </Link>
      </p>
      <h1>Template: {template.name}</h1>
      {isBuiltIn(template) && <p className="import-note">This is this realm's copy of a built-in template. Changes apply to every sheet in this realm that uses it. Keep the licence text: the game's licence requires it.</p>}
      <p className="meta">{sheetCount === 0 ? 'No sheets use it yet.' : `${sheetCount} ${sheetCount === 1 ? 'sheet uses' : 'sheets use'} it, and follow what's saved here.`}</p>
      <div className="actions">
        <button type="button" className="primary" disabled={!dirty || saving} onClick={() => void save()} title="Save the template - every sheet that uses it follows">
          {saving ? 'Saving…' : 'Save template'}
        </button>
        <button type="button" aria-expanded={preview !== null} onClick={() => setPreview(preview ? null : new Map())} title="Try a blank sheet from the template as it stands - nothing is saved">
          {preview ? 'Close the preview' : 'Preview'}
        </button>
        {dirty && <span className="autosave dirty">Unsaved changes</span>}
      </div>
      {blocker.status === 'blocked' && (
        <div className="panel confirm" role="alertdialog" aria-label="Unsaved changes">
          <p>Leave without saving this template? Your changes to it will be lost.</p>
          <div className="actions">
            <button type="button" className="danger" onClick={() => blocker.proceed()}>
              Leave
            </button>
            <button type="button" onClick={() => blocker.reset()}>
              Stay
            </button>
          </div>
        </div>
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {preview && (
        <section className="panel template-preview" aria-label="Preview">
          <p className="import-note">A blank sheet from this template, as it stands now. Try it out: nothing here is saved.</p>
          <SheetForm
            template={built}
            values={preview}
            onChange={(key, value) =>
              setPreview((v) => {
                const next = new Map(v);
                if (value === '') next.delete(key);
                else next.set(key, value);
                return next;
              })
            }
            onRolled={(field, result) => recordRoll(`Preview: ${field.label}`, result)}
          />
          {built.license && (
            <p className="sheet-license">
              {built.name} · {built.license}
            </p>
          )}
        </section>
      )}

      <div className="template-layout">
        <nav className="template-outline" aria-label="Outline">
          <ul>
            <li>
              <button type="button" className={selection.kind === 'template' ? 'outline-item selected' : 'outline-item'} aria-current={selection.kind === 'template' || undefined} onClick={() => setSelection({ kind: 'template' })}>
                ⚙ {draft.name || 'Untitled Template'}
              </button>
            </li>
            {draft.sections.map((section) => (
              <li key={section.uid}>
                <button
                  type="button"
                  className={selection.kind === 'section' && selection.uid === section.uid ? 'outline-item selected' : 'outline-item'}
                  aria-current={(selection.kind === 'section' && selection.uid === section.uid) || undefined}
                  onClick={() => setSelection({ kind: 'section', uid: section.uid })}
                >
                  {section.title || '(untitled section)'} ({section.fields.length})
                </button>
                <ul>
                  {section.fields.map((f) => (
                    <li key={f.uid}>
                      <button
                        type="button"
                        className={selection.kind === 'field' && selection.uid === f.uid ? 'outline-item field selected' : 'outline-item field'}
                        aria-current={(selection.kind === 'field' && selection.uid === f.uid) || undefined}
                        onClick={() => setSelection({ kind: 'field', uid: f.uid })}
                      >
                        {f.field.label} · {FIELD_TYPES[f.field.type]} · @{f.field.key}
                      </button>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          <div className="actions">
            <button
              type="button"
              title="Add a section after the selected one"
              onClick={() => {
                const uid = nextUid++;
                const fieldUid = nextUid++;
                change((next) => {
                  const section: SectionDraft = { uid, title: 'New Section', columns: 2, fields: [newField(next, fieldUid)] };
                  const index = sectionIndexOf(next, selection) + 1;
                  next.sections.splice(index <= 0 ? next.sections.length : index, 0, section);
                });
                setSelection({ kind: 'section', uid });
              }}
            >
              + Section
            </button>
            <button
              type="button"
              title="Add a field after the selected one"
              onClick={() => {
                const uid = nextUid++;
                const sectionUid = nextUid++;
                change((next) => {
                  if (next.sections.length === 0) next.sections.push({ uid: sectionUid, title: 'Main', columns: 2, fields: [] });
                  const section = next.sections[Math.max(0, sectionIndexOf(next, selection))]!;
                  const at = selection.kind === 'field' ? section.fields.findIndex((f) => f.uid === selection.uid) + 1 : section.fields.length;
                  section.fields.splice(at, 0, newField(next, uid));
                });
                setSelection({ kind: 'field', uid });
              }}
            >
              + Field
            </button>
            <button type="button" aria-label="Move up" title="Move the selected section or field up" disabled={selection.kind === 'template'} onClick={() => change((next) => move(next, selection, -1))}>
              ↑
            </button>
            <button type="button" aria-label="Move down" title="Move the selected section or field down" disabled={selection.kind === 'template'} onClick={() => change((next) => move(next, selection, 1))}>
              ↓
            </button>
            <button
              type="button"
              title="Remove the selected section or field"
              disabled={selection.kind === 'template'}
              onClick={() => {
                change((next) => {
                  if (selection.kind === 'section') next.sections = next.sections.filter((s) => s.uid !== selection.uid);
                  else if (selection.kind === 'field') for (const s of next.sections) s.fields = s.fields.filter((f) => f.uid !== selection.uid);
                });
                setSelection({ kind: 'template' });
              }}
            >
              Remove
            </button>
          </div>
        </nav>

        <div className="template-properties">
          {selection.kind === 'template' || (!selectedSection && !selectedField) ? (
            <TemplateForm draft={draft} change={change} />
          ) : selectedSection ? (
            <SectionForm key={selectedSection.uid} section={selectedSection} change={change} />
          ) : (
            <FieldForm key={selectedField!.uid} draft={draft} uid={selectedField!.uid} change={change} />
          )}
          <h2>Checks</h2>
          {problems.length === 0 ? (
            <p className="import-note">✓ No problems found.</p>
          ) : (
            <ul className="template-problems" aria-label="Problems">
              {problems.map((problem, i) => (
                <li key={i}>⚠ {problem}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </article>
  );
}

type Change = (work: (next: Draft) => void) => void;

function TemplateForm({ draft, change }: { draft: Draft; change: Change }) {
  const keys = allDrafts(draft).map((f) => f.field.key);
  return (
    <div className="template-form">
      <h2>Template</h2>
      <label>
        Name
        <input value={draft.name} title="The template's name, shown on its card and every sheet" onChange={(e) => {
          const value = e.currentTarget.value;
          change((d) => void (d.name = value));
        }} />
      </label>
      <label>
        System
        <input value={draft.system} placeholder="e.g. SRD 5.1, Fate, homebrew" title="The game system it's for" onChange={(e) => {
          const value = e.currentTarget.value;
          change((d) => void (d.system = value));
        }} />
      </label>
      <label>
        Description
        <textarea rows={3} value={draft.description} title="What the template is for" onChange={(e) => {
          const value = e.currentTarget.value;
          change((d) => void (d.description = value));
        }} />
      </label>
      <label>
        Licence
        <textarea rows={4} value={draft.license} placeholder="The attribution the game's licence requires, shown on every sheet." onChange={(e) => {
          const value = e.currentTarget.value;
          change((d) => void (d.license = value));
        }} />
      </label>
      <label>
        Conditions
        <textarea rows={4} value={draft.conditions} placeholder="One per line, offered on each sheet (Prone, Frightened...)" onChange={(e) => {
          const value = e.currentTarget.value;
          change((d) => void (d.conditions = value));
        }} />
      </label>
      {SHEET_ROLES.map((role) => (
        <label key={role}>
          {ROLE_NAMES[role]} field
          <select
            value={draft.roles[role] ?? ''}
            title={`Which field is the character's ${ROLE_NAMES[role].toLowerCase()} - entries read it`}
            onChange={(e) => {
              const key = e.currentTarget.value;
              change((d) => {
                if (key === '') delete d.roles[role];
                else d.roles[role] = key;
              });
            }}
          >
            <option value="">(none)</option>
            {[...new Set(keys)].map((key) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </select>
        </label>
      ))}
      <p className="meta">Roles tell the rest of Realmery which fields matter: an entry shows all three, and rolls the Initiative field.</p>
    </div>
  );
}

function SectionForm({ section, change }: { section: SectionDraft; change: Change }) {
  const edit = (work: (s: SectionDraft) => void) =>
    change((d) => {
      const target = d.sections.find((s) => s.uid === section.uid);
      if (target) work(target);
    });
  return (
    <div className="template-form">
      <h2>Section</h2>
      <label>
        Title
        <input value={section.title} title="The section's heading on the sheet" onChange={(e) => {
          const title = e.currentTarget.value;
          edit((s) => void (s.title = title));
        }} />
      </label>
      <label>
        Columns
        <input
          type="number"
          min={1}
          max={4}
          value={section.columns}
          title="How many columns the section's fields are laid out in"
          onChange={(e) => {
            const columns = Math.max(1, Math.min(4, Math.round(Number(e.currentTarget.value)) || 1));
            edit((s) => void (s.columns = columns));
          }}
        />
      </label>
    </div>
  );
}

const DEFAULT_LABELS: Partial<Record<SheetFieldType, string>> = { DICE: 'Starting roll', CHECKBOX: 'Checked (true)', CHOICE: 'Default option' };

function FieldForm({ draft, uid, change }: { draft: Draft; uid: number; change: Change }) {
  const current = allDrafts(draft).find((f) => f.uid === uid)!.field;
  const [keyText, setKeyText] = useState(current.key);
  const committedKey = useRef(current.key);
  const ids = { key: useId() };
  const type = current.type;
  const text = type === 'TEXT' || type === 'LONG_TEXT';

  const edit = (work: (f: FieldDraft, d: Draft) => void) =>
    change((d) => {
      const target = allDrafts(d).find((f) => f.uid === uid);
      if (target) work(target, d);
    });

  /** A key is committed on Enter or leaving the box, not per keystroke, so passing through another field's key never rewrites its references. */
  function commitKey(typed: string) {
    const newKey = javaStrip(typed);
    const oldKey = committedKey.current;
    if (newKey === oldKey) return;
    edit((f, d) => {
      const taken = uniqueKeyExcept(d, newKey, uid) !== newKey;
      if (!taken && newKey !== '') renameReferences(d, oldKey, newKey);
      f.field = { ...f.field, key: newKey };
    });
    committedKey.current = newKey;
  }

  const set = (part: Partial<SheetField>) => edit((f) => void (f.field = { ...f.field, ...part }));

  const defaultLabel = type === 'RESOURCE' ? (current.formula === '' ? 'Starting max' : 'Starts at') : (DEFAULT_LABELS[type] ?? 'Default');
  const hint =
    type === 'DERIVED'
      ? 'Worked out from other fields, like (@str - 10) / 2. Division rounds down.'
      : type === 'RESOURCE'
        ? `A current value out of a maximum. Leave Maximum blank to let each sheet enter its own; formulas read @${keyText} (current) and @${keyText}_max.`
        : type === 'CHOICE'
          ? 'One option per line. Write Trained=2 to give an option a value in formulas; otherwise an option is worth its position, from 0.'
          : type === 'DICE'
            ? 'The player writes their own roll, like 1d8 + @str_mod.'
            : type === 'CHECKBOX'
              ? 'Reads as 1 when checked and 0 when not.'
              : `Formulas and rolls read this field as @${keyText}.`;

  return (
    <div className="template-form">
      <h2>Field</h2>
      <label>
        Label
        <input
          value={current.label}
          title="The field's name on the sheet"
          onChange={(e) => {
            const label = e.currentTarget.value;
            // A key that was made from the label follows it; one typed by hand stays put.
            if (keyText === keyFor(current.label)) {
              const followed = uniqueKeyExcept(draft, keyFor(label), uid);
              setKeyText(followed);
              commitKey(followed);
            }
            set({ label });
          }}
        />
      </label>
      <label htmlFor={ids.key}>
        Key
        <input
          id={ids.key}
          className="code"
          value={keyText}
          title="What formulas and rolls call this field, as @key - renaming it updates them"
          onChange={(e) => setKeyText(e.currentTarget.value)}
          onBlur={() => commitKey(keyText)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitKey(keyText);
          }}
        />
      </label>
      <label>
        Type
        <select value={type} title="What kind of field: a number, text, checkbox, choice, resource, derived, or roll" onChange={(e) => set({ type: e.currentTarget.value as SheetFieldType })}>
          {(Object.keys(FIELD_TYPES) as SheetFieldType[]).map((t) => (
            <option key={t} value={t}>
              {FIELD_TYPES[t]}
            </option>
          ))}
        </select>
      </label>
      {type !== 'DERIVED' && (
        <label>
          {defaultLabel}
          <input className={type === 'DICE' ? 'code' : undefined} value={current.defaultValue} title="What a new sheet starts with" onChange={(e) => set({ defaultValue: e.currentTarget.value })} />
        </label>
      )}
      {(type === 'DERIVED' || type === 'RESOURCE') && (
        <label>
          {type === 'RESOURCE' ? 'Maximum' : 'Formula'}
          <input className="code" value={current.formula} title="A formula from other fields, like (@str - 10) / 2 - for a resource, its maximum" onChange={(e) => set({ formula: e.currentTarget.value })} />
        </label>
      )}
      {!text && type !== 'DICE' && (
        <label>
          Roll
          <input className="code" value={current.roll} placeholder={`e.g. 1d20 + @${current.key}`} title="Makes the field rollable: a 🎲 beside it rolls this" onChange={(e) => set({ roll: e.currentTarget.value })} />
        </label>
      )}
      {type === 'CHOICE' && (
        <label>
          Options
          <textarea
            rows={4}
            defaultValue={current.options.join('\n')}
            title="A choice's options, one per line - Trained=2 gives one a value"
            onChange={(e) => set({ options: javaLines(e.currentTarget.value).map(javaStrip).filter((o) => o !== '') })}
          />
        </label>
      )}
      <label>
        Help
        <input value={current.help} title="The hint shown when hovering over the field on a sheet" onChange={(e) => set({ help: e.currentTarget.value })} />
      </label>
      <p className="meta">{hint}</p>
    </div>
  );
}

